import React from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { ComboBox } from 'components/ComboBox/ComboBox'
import { Item } from '@react-stately/collections'
import Styles from './TimePicker.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { formatTo12Hour, getTimezoneAbbreviation } from './utils'

interface TimePickerProps {
  value: Dayjs | undefined
  onChange: (newValue: Dayjs) => void
  isDisabled: boolean
}

export const TimePicker: React.FC<TimePickerProps> = ({ value, onChange, isDisabled }) => {
  const hoursAndMinutes = Array.from({ length: 12 * 4 }, (_, i) => {
    const hour = i < 4 ? 12 : Math.floor((i - 4) / 4) + 1
    const minute = (i % 4) * 15
    return `${hour}:${minute.toString().padStart(2, '0')}`
  })

  const periods = ['AM', 'PM']

  const handleTimeChange = (timeValue: string, period: string) => {
    const [hourStr, minuteStr] = timeValue.split(':')
    const hour = parseInt(hourStr)
    const minute = parseInt(minuteStr)

    // Adjust hour for 12-hour format
    const adjustedHour = period === 'PM' ? (hour % 12) + 12 : hour % 12
    let newTime = dayjs().hour(adjustedHour).minute(minute).second(0)

    onChange(newTime)
  }

  const renderComboBox = (
    items: string[],
    selectedKey: string | undefined,
    label: string,
    onChange: (value: string) => void,
    styleName: string,
  ) => (
    <ComboBox
      wrapperClassName={Styles[styleName]}
      popoverClassName={Styles.comboBoxMenu}
      popoverMaxHeight={180}
      menuTrigger="focus"
      focusOnIconClick
      aria-label={label}
      placeholder={label}
      size="lg"
      disabled={isDisabled}
      items={items.map((item) => ({ id: item, value: item }))}
      selectedKey={selectedKey}
      onSelectionChange={(key) => onChange(key as string)}
      allowsCustomValue
    >
      {(item) => <Item key={item.id}>{item.value}</Item>}
    </ComboBox>
  )

  return (
    <div className={Styles.timePicker}>
      <Text variant="label">Time of Day ({getTimezoneAbbreviation()})</Text>
      <div className={Styles.comboBoxes}>
        {renderComboBox(
          hoursAndMinutes,
          formatTo12Hour(value),
          'Select Time',
          (selectedTime) => handleTimeChange(selectedTime, value?.format('A') ?? ''),
          'hourMinutePicker',
        )}
        {renderComboBox(
          periods,
          value?.format('A'),
          'AM',
          (selectedPeriod) => handleTimeChange(value?.format('HH:mm') ?? '', selectedPeriod),
          'ampmPicker',
        )}
      </div>
    </div>
  )
}
